import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  Cpu,
  Layers,
  Radio,
  Calendar,
  Users,
  Car as CarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import MethodGlyph from "@/components/MethodGlyph";

export default function EcuDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const page = Number(searchParams.get("page") || 1);
  const [manufacturerFilter, setManufacturerFilter] = useState(
    searchParams.get("mfr") || ""
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const decoded = decodeURIComponent(name);
        const params = {
          name: decoded,
          vehicles_page: page,
          vehicles_page_size: 20,
        };
        if (manufacturerFilter) params.manufacturer = manufacturerFilter;
        const { data: d } = await api.get("/ecu", { params });
        if (!cancelled) setData(d);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [name, page, manufacturerFilter]);

  const setPage = (p) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setSearchParams(next, { replace: true });
  };

  const applyMfr = (mfr) => {
    setManufacturerFilter(mfr);
    const next = new URLSearchParams(searchParams);
    if (mfr) next.set("mfr", mfr);
    else next.delete("mfr");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("In Zwischenablage kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const showingRange = useMemo(() => {
    if (!data?.vehicles) return "0";
    const t = data.vehicles.total;
    if (!t) return "0";
    const size = data.vehicles.page_size;
    const from = (data.vehicles.page - 1) * size + 1;
    const to = Math.min(data.vehicles.page * size, t);
    return `${from}–${to}`;
  }, [data]);

  if (loading) {
    return (
      <div data-testid="ecu-loading" className="space-y-4">
        <div className="h-32 bg-panel rounded animate-pulse" />
        <div className="h-64 bg-panel rounded animate-pulse" />
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div data-testid="ecu-not-found" className="text-center py-20">
        <div className="text-neutral-500 text-sm">ECU nicht gefunden.</div>
        <Button
          onClick={() => navigate("/")}
          className="mt-4 bg-neutral-800 hover:bg-neutral-700 text-neutral-100"
        >
          Zurück zur Liste
        </Button>
      </div>
    );
  }

  const { stats, vehicles, detail, other_ecus_same_protocol } = data;

  return (
    <div data-testid="ecu-page" className="space-y-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-neutral-500 font-mono">
        <Link
          to="/"
          data-testid="ecu-back"
          className="hover:text-[#EB4E4E] inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Compatibility
        </Link>
        <span>/</span>
        <span className="text-neutral-400">ECU</span>
        <span>/</span>
        <span className="text-neutral-200">{data.name}</span>
      </div>

      {/* Hero: horizontal "spec sheet" */}
      <section className="relative rounded-2xl border border-neutral-800 bg-gradient-to-br from-panel to-overlay overflow-hidden">
        {/* Traces / PCB decoration */}
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-30">
          <svg width="100%" height="100%" viewBox="0 0 1400 400" preserveAspectRatio="none">
            <g stroke="#D32222" strokeWidth="0.7" fill="none" opacity="0.35">
              <path d="M0 200 L 300 200 L 340 240 L 700 240 L 740 200 L 1100 200 L 1140 240 L 1400 240" />
              <path d="M0 260 L 200 260 L 240 300 L 800 300 L 840 260 L 1400 260" />
              <path d="M0 140 L 500 140 L 540 100 L 900 100 L 940 140 L 1400 140" />
              <circle cx="340" cy="240" r="3" />
              <circle cx="740" cy="200" r="3" />
              <circle cx="1140" cy="240" r="3" />
              <circle cx="540" cy="100" r="3" />
              <circle cx="840" cy="260" r="3" />
            </g>
          </svg>
        </div>

        <div className="relative p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-3 flex items-center gap-2">
              <Cpu className="w-3 h-3" />
              {data.type === "TCU" ? "Transmission Control Unit" : "Engine Control Unit"}
            </div>
            <h1
              data-testid="ecu-name"
              className="text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-neutral-50 break-words"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {data.name}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {data.protocol && (
                <Badge
                  data-testid="ecu-protocol"
                  variant="outline"
                  className="border-[#D32222]/40 text-[#EB4E4E] bg-[#D32222]/10 font-mono text-xs px-2.5 py-1"
                >
                  <Radio className="w-3 h-3 mr-1.5" /> {data.protocol}
                </Badge>
              )}
              {data.type && (
                <Badge
                  data-testid="ecu-type-badge"
                  variant="outline"
                  className="border-neutral-700 text-neutral-200 bg-neutral-900 font-mono text-xs px-2.5 py-1"
                >
                  <Layers className="w-3 h-3 mr-1.5" /> {data.type}
                </Badge>
              )}
              {stats.methods.map((m) => (
                <span
                  key={m}
                  data-testid={`ecu-method-${m}`}
                  className="inline-flex items-center"
                  title={m}
                >
                  <MethodGlyph label={m} className="w-9 h-9" />
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              {data.url && (
                <a
                  href={data.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="ecu-external-link"
                  className="inline-flex items-center gap-1.5 text-sm text-[#EB4E4E] hover:text-[#F26060] transition"
                >
                  Original auf autotuner.com <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {data.url && (
                <button
                  data-testid="ecu-copy-url"
                  onClick={() => copy(data.url)}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-200 transition"
                >
                  <Copy className="w-3 h-3" /> URL kopieren
                </button>
              )}
            </div>
          </div>

          {/* Right column: stats */}
          <div className="grid grid-cols-2 gap-3 lg:min-w-[320px]">
            <MegaStat
              icon={<CarIcon className="w-4 h-4" />}
              value={stats.vehicle_count}
              label="Fahrzeuge"
              testid="ecu-stat-vehicles"
            />
            <MegaStat
              icon={<Users className="w-4 h-4" />}
              value={stats.unique_manufacturers}
              label="Hersteller"
              testid="ecu-stat-mfr"
            />
            <MegaStat
              icon={<Calendar className="w-4 h-4" />}
              value={
                stats.year_range
                  ? `${stats.year_range[0]}–${stats.year_range[1]}`
                  : "—"
              }
              label="Jahre"
              small
              testid="ecu-stat-years"
            />
            <MegaStat
              icon={<Layers className="w-4 h-4" />}
              value={stats.methods.length}
              label="Methoden"
              testid="ecu-stat-methods"
            />
          </div>
        </div>
      </section>

      {/* Description */}
      {detail?.description ? (
        <section
          data-testid="ecu-description"
          className="rounded-2xl border border-neutral-800 bg-panel p-6 lg:p-8"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-3">
            Über diesen Chip
          </div>
          <p className="text-neutral-300 leading-relaxed max-w-3xl text-sm sm:text-base">
            {detail.description}
          </p>
        </section>
      ) : (
        <section className="rounded-2xl border border-neutral-800/60 bg-panel/60 p-4 text-xs text-neutral-500">
          Keine Detail-Beschreibung im Cache. Der Details-Spider konnte die
          Original-Seite dieses ECUs nicht abrufen (429 vom Zielhost).{" "}
          <Link to="/scrape" className="text-[#EB4E4E] underline decoration-dotted">
            Details-Crawl mit Proxies erneut starten
          </Link>
          .
        </section>
      )}

      {/* Manufacturers pills */}
      {stats.manufacturers.length > 0 && (
        <section data-testid="ecu-manufacturers" className="space-y-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222]">
            Hersteller-Filter
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="ecu-mfr-pill-all"
              onClick={() => applyMfr("")}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${
                !manufacturerFilter
                  ? "border-[#D32222]/60 bg-[#D32222]/15 text-[#EB4E4E]"
                  : "border-neutral-800 bg-panel text-neutral-400 hover:text-neutral-200"
              }`}
            >
              Alle · {stats.vehicle_count}
            </button>
            {stats.manufacturers.map((m) => (
              <button
                key={m}
                data-testid={`ecu-mfr-pill-${m}`}
                onClick={() => applyMfr(m)}
                className={`text-xs px-3 py-1.5 rounded-md border transition ${
                  manufacturerFilter === m
                    ? "border-[#D32222]/60 bg-[#D32222]/15 text-[#EB4E4E]"
                    : "border-neutral-800 bg-panel text-neutral-400 hover:text-neutral-200"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Vehicles table */}
      <section className="space-y-3">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222]">
              Kompatibilitätsliste
            </div>
            <h2
              className="text-2xl sm:text-3xl text-neutral-100 mt-1"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              Fahrzeuge mit {data.name}
              {manufacturerFilter && (
                <span className="text-neutral-500"> · {manufacturerFilter}</span>
              )}
            </h2>
          </div>
          <div className="text-xs text-neutral-500">
            Zeige{" "}
            <span className="text-neutral-200">{showingRange}</span> von{" "}
            <span className="text-neutral-200">{vehicles.total}</span>
          </div>
        </div>

        <div className="rounded-xl border border-neutral-800 bg-panel overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-neutral-800">
            <div className="col-span-3">Manufacturer</div>
            <div className="col-span-3">Model</div>
            <div className="col-span-4">Engine</div>
            <div className="col-span-2">Methods</div>
          </div>
          <div
            data-testid="ecu-vehicles-list"
            className="divide-y divide-neutral-900"
          >
            {vehicles.items.map((row) => (
              <VehicleRow key={row.id} row={row} />
            ))}
            {vehicles.items.length === 0 && (
              <div className="px-4 py-12 text-center text-neutral-500 text-sm">
                Keine Fahrzeuge für diesen Filter.
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-neutral-500">
            Seite {vehicles.page} / {vehicles.total_pages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ecu-prev-page"
              variant="outline"
              size="sm"
              disabled={vehicles.page <= 1}
              onClick={() => setPage(vehicles.page - 1)}
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
            <Button
              data-testid="ecu-next-page"
              variant="outline"
              size="sm"
              disabled={vehicles.page >= vehicles.total_pages}
              onClick={() => setPage(vehicles.page + 1)}
              className="border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-100"
            >
              Weiter <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Sibling ECUs same protocol */}
      {other_ecus_same_protocol.length > 0 && (
        <section
          data-testid="ecu-siblings"
          className="rounded-2xl border border-neutral-800 bg-panel p-6 lg:p-8"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32222] mb-2">
            Andere ECUs mit Protokoll{" "}
            <span className="text-neutral-100 font-mono">{data.protocol}</span>
          </div>
          <h2
            className="text-2xl sm:text-3xl text-neutral-100 mb-5"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Verwandte Chips
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {other_ecus_same_protocol.slice(0, 12).map((e) => (
              <Link
                key={e.name}
                to={`/ecu/${encodeURIComponent(e.name)}`}
                data-testid={`ecu-sibling-${e.name}`}
                className="group rounded-xl border border-neutral-800 bg-overlay/40 p-4 hover:border-[#D32222]/50 transition"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {e.type}
                </div>
                <div
                  className="mt-1 text-lg text-neutral-100 group-hover:text-[#EB4E4E] transition"
                  style={{ fontFamily: "'Orbitron', sans-serif" }}
                >
                  {e.name}
                </div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-2xl text-[#D32222] tabular-nums">
                    {e.vehicle_count}
                  </span>
                  <span className="text-[11px] text-neutral-500">Fahrzeuge</span>
                </div>
                <div className="mt-2 text-[10px] font-mono text-neutral-500 truncate">
                  {e.manufacturers.slice(0, 5).join(", ")}
                  {e.manufacturers.length > 5 && " …"}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function MegaStat({ icon, value, label, small, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-neutral-800 bg-overlay/50 backdrop-blur-sm p-4"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        {icon} {label}
      </div>
      <div
        className={`mt-2 text-neutral-100 tabular-nums ${small ? "text-2xl" : "text-4xl"}`}
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {value}
      </div>
    </div>
  );
}

function VehicleRow({ row }) {
  const model = [row.model, row.version].filter(Boolean).join(" ");
  return (
    <Link
      to={`/vehicle/${encodeURIComponent(row.id)}`}
      data-testid="ecu-vehicle-row"
      className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-3 hover:bg-neutral-900/40 transition text-sm items-center"
    >
      <div className="md:col-span-3 flex items-center gap-3">
        {row.manufacturer_logo && (
          <img
            src={row.manufacturer_logo}
            alt=""
            className="h-12 w-12 object-contain rounded bg-neutral-900 border border-neutral-800 p-1 shrink-0"
          />
        )}
        <span className="text-neutral-100">{row.manufacturer}</span>
      </div>
      <div className="md:col-span-3 text-neutral-200">
        {model || "—"}
        <span className="text-neutral-500 text-xs"> · {row.year || "—"}</span>
      </div>
      <div className="md:col-span-4 text-neutral-300 truncate">
        {row.engine || "—"}
      </div>
      <div className="md:col-span-2 flex flex-wrap gap-1">
        {(row.methods || []).map((m, i) => (
          <span key={i} className="inline-flex items-center" title={m.label}>
            <MethodGlyph label={m.label} className="w-9 h-9" />
          </span>
        ))}
      </div>
    </Link>
  );
}
